import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from '../../components/ui/alert-dialog';
import { Loader2, Plus, PhoneCall, ChevronRight, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  active: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  abandoned: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

const FIT_COLORS = {
  good_fit: 'text-green-400',
  maybe: 'text-yellow-400',
  not_fit: 'text-red-400',
};

function TriageList() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  // The call queued for deletion drives the confirm dialog; null = closed.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/triage/calls`, { params: { limit: 50 } });
        setCalls(res.data.calls || []);
      } catch (e) {
        toast.error('Failed to load calls');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openCall = (c) => {
    navigate(c.status === 'completed' ? `/triage/${c.call_id}/summary` : `/triage/${c.call_id}`);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const callId = pendingDelete.call_id;
    setDeleting(true);
    try {
      await axios.delete(`${API}/triage/calls/${callId}`);
      setCalls((prev) => prev.filter((x) => x.call_id !== callId));
      toast.success('Call deleted');
      setPendingDelete(null);
    } catch (e) {
      toast.error('Failed to delete call');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a] p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-white mb-1">Triage Calls</h1>
          <p className="text-white/50 text-sm">Qualify inbound leads with the NEPQ copilot.</p>
        </div>
        <Button onClick={() => navigate('/triage/new')} className="bg-[#00d4aa] hover:bg-[#00d4aa]/90 text-black font-medium">
          <Plus className="w-4 h-4 mr-1.5" /> New Triage
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-white/40">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : calls.length === 0 ? (
        <Card className="bg-white/5 border-white/10 border-dashed">
          <CardContent className="p-10 text-center">
            <PhoneCall className="w-8 h-8 text-white/20 mx-auto mb-3" />
            <p className="text-white/50 text-sm">No triage calls yet. Start your first one.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {calls.map((c) => (
            <div key={c.call_id}
              className="group glass-morphism rounded-xl flex items-center gap-2 hover:bg-white/5 transition-all">
              <button onClick={() => openCall(c)}
                className="flex-1 min-w-0 text-left px-5 py-4 flex items-center gap-4">
                <PhoneCall className="w-5 h-5 text-[#00d4aa] flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-white truncate">
                    {c.prospect_name || 'Unknown'} {c.prospect_company ? `· ${c.prospect_company}` : ''}
                  </p>
                  <p className="text-xs text-white/40 truncate">
                    {c.trade} · {c.started_at ? new Date(c.started_at).toLocaleDateString() : ''}
                  </p>
                </div>
                {typeof c.fit_score === 'number' && (
                  <span className={`text-xs font-mono ${FIT_COLORS[c.fit_label] || 'text-white/50'}`}>
                    {c.fit_score}/100
                  </span>
                )}
                <Badge className={`${STATUS_COLORS[c.status] || 'bg-white/10 text-white/60'} border`}>{c.status}</Badge>
                <ChevronRight className="w-4 h-4 text-white/30" />
              </button>
              <button
                onClick={() => setPendingDelete(c)}
                aria-label="Delete call"
                title="Delete call"
                className="flex-shrink-0 mr-3 p-2 rounded-lg text-white/25 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
        <AlertDialogContent className="glass-morphism border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete this call?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">
              {pendingDelete?.prospect_name ? `"${pendingDelete.prospect_name}" — ` : ''}This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              className="bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-red-500/90 hover:bg-red-500 text-white">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default TriageList;
